import { createReducer, on } from '@ngrx/store';
import { updateReadOnlyCriteria, updateVisibility } from '@src/suite/common/utils/record-utils';

import { AssignmentRecordActions, DiscoverRecordActions, PublicationRecordActions, RundownRecordActions, StoriesRecordActions, TopicRecordActions, UserRecordActions } from './records.actions';
import { AssignmentsRecordState, DiscoverRecordState, PublicationsRecordState, RundownsRecordState, StoriesRecordState, TopicsRecordState, UsersRecordState } from './records.models';

export const initialTopicState: TopicsRecordState = {
    topics: new Map<string, StorytellingTopicRecord>(),
    topicTemplates: new Map<string, StorytellingTopicRecord>(),
};

export const initialAssignmentsState: AssignmentsRecordState = {
    assignments: new Map(),
    assignmentsByTopic: new Map(),
};

export const initialStoriesState: StoriesRecordState = {
    stories: new Map(),
    storiesByTopic: new Map(),
    storyTemplates: new Map(),
};

export const initialStorytellingUsersState: UsersRecordState = {
    users: new Map<string, StorytellingUserRecord>(),
};

// TODO create and register reducers for these states
export const initialRundownsState: RundownsRecordState = {
    rundowns: new Map<string, StorytellingRundownRecord>(),
};

// TODO create and register reducers for these states
export const initialPublicationsState: PublicationsRecordState = {
    publicationChannels: new Map<string, StorytellingPublicationChannelRecord>(),
    publications: new Map<string, StorytellingPublicationRecord>(),
    publicationList: new Map<string, StorytellingPublicationListRecord>(),
};

export const initialDiscoverRecordState: DiscoverRecordState = {
    discoverItems: new Map<string, StorytellingRecord>(),
};

export const topicRecordsReducer = createReducer(
    initialTopicState,
    on(TopicRecordActions.setTopics, (state, action): TopicsRecordState => {
        action.records.forEach(topic => {
            updateVisibility(topic);
            updateReadOnlyCriteria(topic);
        });
        return {
            ...state,
            topics: action.records,
        };
    }),
    on(TopicRecordActions.updateTopics, (state, { records }): TopicsRecordState => {
        if (!records?.length) {
            return state;
        }

        const topicsMap = records.reduce((acc, topic) => {
            const existingTopic = state.topics.get(topic.Guid) || {};
            Object.keys(existingTopic)
                .filter(key => existingTopic[key]?.timeZone)
                .forEach(key => {
                    if (existingTopic[key]?.timeZone) {
                        topic[key].timeZone = existingTopic[key].timeZone;
                    }
                });
            const updatedTopic = {
                ...existingTopic,
                ...topic,
            };
            updateVisibility(updatedTopic);
            updateReadOnlyCriteria(updatedTopic);
            acc.set(topic.Guid, updatedTopic);
            return acc;
        }, new Map<string, StorytellingTopicRecord>(state.topics));
        return {
            ...state,
            topics: topicsMap,
        };
    }),
    on(TopicRecordActions.setTopicTemplates, (state, action): TopicsRecordState => {
        return {
            ...state,
            topicTemplates: new Map(action.templates.map(story => [story.Guid, story])),
        };
    }),
    on(TopicRecordActions.deleteTopics, (state, { records }): TopicsRecordState => {
        if (!records?.length) {
            return state;
        }

        const topicsMap = new Map<string, StorytellingAssignmentRecord>(state.topics);
        records.forEach(topic => {
            topicsMap.delete(topic.Guid);
        });
        return {
            ...state,
            topics: topicsMap,
        };
    })
);

export const rundownRecordsReducer = createReducer(
    initialRundownsState,
    on(RundownRecordActions.setRundowns, (state, action): RundownsRecordState => {
        action.records.forEach(rundown => {
            updateVisibility(rundown);
            updateReadOnlyCriteria(rundown);
        });
        return {
            ...state,
            rundowns: action.records,
        };
    }),
    on(RundownRecordActions.updateRundowns, (state, { records }): RundownsRecordState => {
        if (!records?.length) {
            return state;
        }

        const rundownsMap = records.reduce((acc, rundown) => {
            const existingRundown = state.rundowns.get(rundown.Guid) || {};
            Object.keys(existingRundown)
                .filter(key => existingRundown[key]?.timeZone)
                .forEach(key => {
                    if (existingRundown[key]?.timeZone) {
                        rundown[key].timeZone = existingRundown[key].timeZone;
                    }
                });
            const updatedRundown = {
                ...existingRundown,
                ...rundown,
            };
            updateVisibility(updatedRundown);
            updateReadOnlyCriteria(updatedRundown);
            acc.set(rundown.Guid, updatedRundown);
            return acc;
        }, new Map<string, StorytellingRundownRecord>(state.rundowns));
        return {
            ...state,
            rundowns: rundownsMap,
        };
    })
);

export const storiesRecordsReducer = createReducer(
    initialStoriesState,
    on(StoriesRecordActions.setStories, (state, action): StoriesRecordState => {
        action.records.forEach(story => {
            story.FullStorySlug = story?.ContainerTitle?.length > 0 ? `${story?.Title || ''} [${story.ContainerTitle} ${story.ContainerModTime}]` : story?.Title || '';
            updateVisibility(story);
            updateReadOnlyCriteria(story);
        });
        return {
            ...state,
            stories: action.records,
            storiesByTopic: Array.from(action.records).reduce((acc, story) => {
                const topicId = story[1].PSTStoryTopicGUID;
                if (topicId) {
                    const current = acc.get(topicId) || new Set<string>();
                    current.add(story[1].Guid);
                    acc.set(topicId, current);
                }
                return acc;
            }, new Map<string, Set<string>>()),
        };
    }),
    on(StoriesRecordActions.updateStories, (state, { records }): StoriesRecordState => {
        if (!records?.length) {
            return state;
        }

        const storiesMap = records.reduce((acc, story) => {
            const existingStory = state.stories.get(story.Guid) || {};
            if (story.Title != null) {
                story.FullStorySlug = story?.ContainerTitle?.length > 0 ? `${story?.Title || ''} [${story.ContainerTitle} ${story.ContainerModTime}]` : story?.Title || '';
            } else {
                delete story.Title;
            }
            if (existingStory?.ModTime?.moment?.isValid && story.ModTime?.moment?.isValid && existingStory.ModTime.moment.isAfter(story.ModTime.moment)) {
                acc.set(existingStory.Guid, existingStory);
                return acc;
            }
            Object.keys(existingStory)
                .filter(key => existingStory[key]?.timeZone)
                .forEach(key => {
                    if (existingStory[key]?.timeZone) {
                        story[key].timeZone = existingStory[key].timeZone;
                    }
                });
            const updatedStory = {
                ...existingStory,
                ...story,
            };
            updateVisibility(updatedStory);
            updateReadOnlyCriteria(updatedStory);
            acc.set(story.Guid, updatedStory);
            return acc;
        }, new Map<string, StorytellingStoryRecord>(state.stories));

        return {
            ...state,
            stories: storiesMap,
            storiesByTopic: Array.from(storiesMap).reduce((acc, st) => {
                const topicId = st[1].PSTStoryTopicGUID;
                if (topicId) {
                    const current = acc.get(topicId) || new Set<string>();
                    current.add(st[1].Guid);
                    acc.set(topicId, current);
                }
                return acc;
            }, new Map<string, Set<string>>()),
        };
    }),
    on(StoriesRecordActions.setStoryTemplates, (state, action): StoriesRecordState => {
        action.templates.forEach(story => {
            story.FullStorySlug = story?.ContainerTitle?.length > 0 ? `${story?.Title || ''} [${story.ContainerTitle} ${story.ContainerModTime}]` : story?.Title || '';
        });
        return {
            ...state,
            storyTemplates: new Map(action.templates.map(story => [story.Guid, story])),
        };
    }),
    on(StoriesRecordActions.deleteStories, (state, { records }): StoriesRecordState => {
        if (!records?.length) {
            return state;
        }

        const storiesMap = new Map<string, StorytellingStoryRecord>(state.stories);
        records.forEach(story => {
            storiesMap.delete(story.Guid);
        });

        return {
            ...state,
            stories: storiesMap,
            storiesByTopic: Array.from(storiesMap).reduce((acc, st) => {
                const topicId = st[1].PSTStoryTopicGUID;
                if (topicId) {
                    const current = acc.get(topicId) || new Set<string>();
                    current.add(st[1].Guid);
                    acc.set(topicId, current);
                }
                return acc;
            }, new Map<string, Set<string>>()),
        };
    })
);

export const assignmentRecordsReducer = createReducer(
    initialAssignmentsState,
    on(AssignmentRecordActions.setAssignments, (state, action): AssignmentsRecordState => {
        action.records.forEach(assignment => {
            updateVisibility(assignment);
            updateReadOnlyCriteria(assignment);
        });
        return {
            ...state,
            assignments: action.records,
            assignmentsByTopic: Array.from(action.records).reduce((acc, assignment) => {
                const topicId = assignment[1].ParentGuid;
                const current = acc.get(topicId) || new Set<string>();
                current.add(assignment[1].Guid);
                acc.set(topicId, current);
                return acc;
            }, new Map<string, Set<string>>()),
        };
    }),
    on(AssignmentRecordActions.updateAssignments, (state, { records }): AssignmentsRecordState => {
        if (!records?.length) {
            return state;
        }

        const assignmentsMap = records.reduce((acc, story) => {
            const existingAssignment = state.assignments.get(story.Guid) || {};
            Object.keys(existingAssignment)
                .filter(key => existingAssignment[key]?.timeZone)
                .forEach(key => {
                    if (existingAssignment[key]?.timeZone) {
                        story[key].timeZone = existingAssignment[key].timeZone;
                    }
                });
            const updatedAssignment = {
                ...existingAssignment,
                ...story,
            };
            updateVisibility(updatedAssignment);
            updateReadOnlyCriteria(updatedAssignment);
            acc.set(story.Guid, updatedAssignment);
            return acc;
        }, new Map<string, StorytellingAssignmentRecord>(state.assignments));

        return {
            ...state,
            assignments: assignmentsMap,
            assignmentsByTopic: Array.from(assignmentsMap).reduce((acc, st) => {
                const topicId = st[1].ParentGuid;
                if (topicId) {
                    const current = acc.get(topicId) || new Set<string>();
                    current.add(st[1].Guid);
                    acc.set(topicId, current);
                }
                return acc;
            }, new Map<string, Set<string>>()),
        };
    }),
    on(AssignmentRecordActions.deleteAssignments, (state, { records }): AssignmentsRecordState => {
        if (!records?.length) {
            return state;
        }

        const assignmentsMap = new Map<string, StorytellingAssignmentRecord>(state.assignments);
        records.forEach(story => {
            assignmentsMap.delete(story.Guid);
        });

        return {
            ...state,
            assignments: assignmentsMap,
            assignmentsByTopic: Array.from(assignmentsMap).reduce((acc, st) => {
                const topicId = st[1].ParentGuid;
                if (topicId) {
                    const current = acc.get(topicId) || new Set<string>();
                    current.add(st[1].Guid);
                    acc.set(topicId, current);
                }
                return acc;
            }, new Map<string, Set<string>>()),
        };
    })
);

export const userRecordsReducer = createReducer(
    initialStorytellingUsersState,
    on(UserRecordActions.setUsers, (state, action): UsersRecordState => {
        return {
            ...state,
            users: action.records,
        };
    }),
    on(UserRecordActions.updateUsers, (state, { records }): UsersRecordState => {
        if (!records?.length) {
            return state;
        }

        const usersMap = records.reduce((acc, user) => {
            const existingUser = state.users.get(user.Guid) || {};
            const updatedUser = {
                ...existingUser,
                ...user,
            };
            // updateVisibility(updatedUser);
            // updateReadOnlyCriteria(updatedUser);
            acc.set(user.Guid, updatedUser);
            return acc;
        }, new Map<string, StorytellingUserRecord>(state.users));

        return {
            ...state,
            users: usersMap,
        };
    })
);

export const discoverRecordsReducer = createReducer(
    initialDiscoverRecordState,
    on(DiscoverRecordActions.setDiscoverItems, (state, action): DiscoverRecordState => {
        return {
            ...state,
            discoverItems: action.records,
        };
    })
);

export const publicationRecordsReducer = createReducer(
    initialPublicationsState,
    on(PublicationRecordActions.setPublications, (state, action): PublicationsRecordState => {
        return {
            ...state,
            publications: action.records,
        };
    }),
    on(PublicationRecordActions.updatePublications, (state, { records }): PublicationsRecordState => {
        if (!records?.length) {
            return state;
        }

        const publicationsMap = records.reduce((acc, publication) => {
            const existingPublication = state.publications.get(publication.Guid) || {};
            const updatedPublication = {
                ...existingPublication,
                ...publication,
            };
            acc.set(publication.Guid, updatedPublication);
            return acc;
        }, new Map<string, StorytellingPublicationRecord>(state.publications));

        return {
            ...state,
            publications: publicationsMap,
        };
    }),
    on(PublicationRecordActions.setPublicationChannels, (state, action): PublicationsRecordState => {
        return {
            ...state,
            publicationChannels: action.records,
        };
    }),
    on(PublicationRecordActions.updatePublicationChannels, (state, action): PublicationsRecordState => {
        if (!action.records?.length) {
            return state;
        }

        const publicationChannelsMap = action.records.reduce((acc, channel) => {
            const existingChannel = state.publicationChannels.get(channel.Guid) || {};
            const updatedChannel = {
                ...existingChannel,
                ...channel,
            };
            acc.set(channel.Guid, updatedChannel);
            return acc;
        }, new Map<string, StorytellingPublicationChannelRecord>(state.publicationChannels));

        return {
            ...state,
            publicationChannels: publicationChannelsMap,
        };
    }),
    on(PublicationRecordActions.setPublicationList, (state, action): PublicationsRecordState => {
        return {
            ...state,
            publicationList: action.records,
        };
    }),
    on(PublicationRecordActions.updatePublicationList, (state, action): PublicationsRecordState => {
        if (!action.records?.length) {
            return state;
        }

        const publicationListMap = action.records.reduce((acc, list) => {
            const existingList = state.publicationList.get(list.Guid) || {};
            const updatedList = {
                ...existingList,
                ...list,
            };
            acc.set(list.Guid, updatedList);
            return acc;
        }, new Map<string, StorytellingPublicationListRecord>(state.publicationList));

        return {
            ...state,
            publicationList: publicationListMap,
        };
    })
);
